import { DOCUMENT, PRECONNECT_CHECK_BLOCKLIST } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { EnvironmentProviders, provideBrowserGlobalErrorListeners, Provider, provideZoneChangeDetection } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, RouteReuseStrategy, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { CustomRouteReuseStrategy, FU_CONFIG, FU_TOAST_CONFIG, FU_TOAST_DEFAULT_CONFIG, TranslateLoaderService } from '@hglobal/fin-ui';
import { provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideNgxMask } from 'ngx-mask';

import { environment } from '@zeusm/environment';
import { getPersonalApiHost } from '@zeusm/shared/core/helpers';
import {
  appErrorsInterceptor,
  businessProfileInterceptor,
  credentialsInterceptor,
  pollingInterceptor,
  traceInterceptor,
} from '@zeusm/shared/core/init';
import { GlobalDialogService, GlobalToastService } from '@zeusm/shared/core/services';
import { PageTitleStore } from '@zeusm/shared/store';

import { appRoutes } from './app.routes';
import { BUSINESS_INFRASTRUCTURE_PROVIDERS } from './business/business.providers';
import { PERSONAL_INFRASTRUCTURE_PROVIDERS } from './personal/personal.providers';
import { SESSION_INFRASTRUCTURE_PROVIDERS } from './shared/session/session.providers';

const isLocalMode = location.hostname === 'localhost';

const FU = [
  {
    provide: FU_CONFIG,
    useValue: {
      apiHost: getPersonalApiHost(),
      useMocks: environment.useMocks,
      production: environment.production,
    },
  },
  {
    provide: FU_TOAST_CONFIG,
    useValue: {
      ...FU_TOAST_DEFAULT_CONFIG,
      closeIconName: 'close',
      position: {
        top: 82,
        right: 24,
      },
    },
  },
];

const NGX = [
  provideTranslateService({
    lang: 'en',
    fallbackLang: 'en',
    loader: provideTranslateLoader(TranslateLoaderService),
  }),
  provideNgxMask(),
];

export const ROUTING_CONFIG = [
  provideRouter(
    appRoutes,
    withRouterConfig({
      paramsInheritanceStrategy: 'always',
    }),
  ),
  {
    provide: RouteReuseStrategy,
    useClass: CustomRouteReuseStrategy,
  },
];

const APP_STATE_PROVIDERS: (Provider | EnvironmentProviders)[] = [PageTitleStore];

export const CUSTOM_PROVIDERS_CONFIG = [
  ...NGX,
  ...FU,
  ...SESSION_INFRASTRUCTURE_PROVIDERS,
  ...PERSONAL_INFRASTRUCTURE_PROVIDERS,
  ...BUSINESS_INFRASTRUCTURE_PROVIDERS,
  ...APP_STATE_PROVIDERS,
  {
    provide: PRECONNECT_CHECK_BLOCKLIST,
    useFactory: (document: Document) => document.defaultView?.location.origin ?? '',
    deps: [DOCUMENT],
  },
  provideHttpClient(
    withInterceptors([credentialsInterceptor, businessProfileInterceptor, pollingInterceptor, appErrorsInterceptor, traceInterceptor]),
  ),
  provideZoneChangeDetection({ eventCoalescing: true }),
  provideBrowserGlobalErrorListeners(),
  provideAnimations(),
  provideServiceWorker('ngsw-worker.js', {
    enabled: !isLocalMode,
    registrationStrategy: 'registerWhenStable:30000',
  }),
  provideNativeDateAdapter(),
];

export const SERVICES_PROVIDES = [GlobalDialogService, GlobalToastService];
